<script lang="ts" setup>
import { i18n } from '@/i18n';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import { TableContentProperty } from '../interface/tableContentHeaders';
import { BaseTableContentViewModel } from '../baseTableContentViewModel';
import { TableContentItem } from '../interface/tableContentItems';
import TableCell from './TableCell.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { convertIDateToDateFormat, convertUnixTimeToIDate } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { computed } from 'vue';
import { TableType } from '@/coreDomainsModule/table';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';

// Propsの定義
const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    headerIndex: number;
    accumulationTableContentWidth: number[];
    stableColumnCount: number;
    item: TableContentItem;
    isEditing: boolean;
    isBold: boolean;
    isChecked: boolean;
    isCheckedBusinessId: boolean;
    isExpanded: boolean;
    tableType: TableType;
  }>(),
  {}
);

const emits = defineEmits<{
  (e: 'onCheckboxSelected', displayId: string): void;
  (e: 'setNarrowState', header: TableContentProperty, itemValue: number): void;
  (e: 'onClickedExpansion', isExpanded: boolean): void;
  (e: 'onCheckboxSelectedBusinessId', businessId: string): void;
}>();

const t = i18n.t;

const baseTableContentVM = new BaseTableContentViewModel();

const textAlign = computed(() => {
  if (['choiceBusinessId', 'choice'].includes(props.header.key)) {
    return 'center';
  }
  return props.header.propertyPath?.propertyType === 'number' ? 'right' : 'left';
});

const getSupplierForecastResponsibleUserNames = computed(() => {
  if (!props.item?.aggregateBusinessResponsibleUsers || props.item.aggregateBusinessResponsibleUsers.length === 0) {
    return '--';
  }
  return props.item.aggregateBusinessResponsibleUsers.map((user: UserAccount) => UserAccountService.getDisplayName(user)).join(', ');
});
const getCustomerForecastResponsibleUserNames = computed(() => {
  if (!props.item?.baseBusinessResponsibleUsers || props.item.baseBusinessResponsibleUsers.length === 0) {
    return '--';
  }
  return props.item.baseBusinessResponsibleUsers.map((user: UserAccount) => UserAccountService.getDisplayName(user)).join(', ');
});

const getToolTipValue = computed(() => {
  if (props.header.key === 'supplierForecastResponsibleUser') {
    return getSupplierForecastResponsibleUserNames.value;
  }
  if (props.header.key === 'customerForecastResponsibleUser') {
    return getCustomerForecastResponsibleUserNames.value;
  }
  return props.item[props.header.key];
});

/**
 * セル表示用に値を整形する
 */
const displayCellValue = computed(() => {
  const value = props.item[props.header.key];
  if (value == null) {
    return '--';
  }

  return value;
});
</script>

<template>
  <TableCell
    :class="{ 'sticky-content': baseTableContentVM.isSticky(stableColumnCount, headerIndex) }"
    :style="{
      left: baseTableContentVM.calcStickyDiff(stableColumnCount, headerIndex, accumulationTableContentWidth[headerIndex]),
    }"
    class="table-cell base-table__body--fixed"
    :text-align="textAlign"
    :table-content-property="header"
    :tool-tip-value="getToolTipValue"
    :is-highlighted="props.isChecked || props.isCheckedBusinessId"
  >
    <template v-if="header.key === 'choice'">
      <RowCheckBox
        v-if="tableType === 'firmOrderTable' || (!isEditing && !item.isVirtualChild)"
        :is-checked="isChecked"
        class="md-primary all-check-button"
        @change-status="$emit('onCheckboxSelected', item?.displayId)"
      />
    </template>
    <template v-else-if="header.key === 'choiceBusinessId'">
      <RowCheckBox
        v-if="!isEditing && !item.isVirtualChild"
        :is-checked="isCheckedBusinessId"
        class="md-primary all-check-button"
        @change-status="$emit('onCheckboxSelectedBusinessId', item?.businessId)"
      />
    </template>
    <template v-else-if="header.key === 'expansion'">
      <!-- 展開ボタン -->
      <SvgLoader
        class="expansion-button"
        :name="isExpanded ? 'arrowDownSize24' : 'chevronRight'"
        @click="emits('onClickedExpansion', !isExpanded)"
      />
    </template>
    <template v-else-if="header.key === 'businessUpdateAt'">
      <span class="table-cell-content" :class="{ 'table-cell-content': isBold }" @click="$emit('setNarrowState', header, item.updateAt)">
        <!-- 更新日情報は表示情報の変換処理を挟む -->
        {{ convertIDateToDateFormat(convertUnixTimeToIDate(item.updatedAt)) }}
      </span>
    </template>
    <template v-else-if="header.key === 'supplierForecastResponsibleUser'">
      <span class="table-cell-content" :class="{ 'table-cell-content': isBold }" @click="$emit('setNarrowState', header, item[header.key])">
        {{ getSupplierForecastResponsibleUserNames }}
      </span>
    </template>
    <template v-else-if="header.key === 'customerForecastResponsibleUser'">
      <span class="table-cell-content" :class="{ 'table-cell-content': isBold }" @click="$emit('setNarrowState', header, item[header.key])">
        {{ getCustomerForecastResponsibleUserNames }}
      </span>
    </template>
    <template v-else-if="header.key === 'headerModify'">
      <span />
    </template>
    <template v-else>
      <span class="table-cell-content" :class="{ 'table-cell-content': isBold }" @click="$emit('setNarrowState', header, item[header.key])">
        {{ displayCellValue }}
      </span>
    </template>
  </TableCell>
</template>

<style scoped lang="scss">
.expansion-button {
  cursor: pointer;
  transition: transform 0.3s;
}

.table-cell-content {
  font-family: Noto Sans JP;
  font-size: $fontSizeSS;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--bold {
    font-weight: bold;
  }
}
</style>
